import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  FacilityDictionaryClient,
  SearchFacilitiesDictionaryRequest,
} from '../../../core/openapi/bff-admin-api';
import { ToastService } from '@ui-lib/components';
import { exhaustMap, map, Observable, of, startWith, Subject, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IDictionaryItem } from '../../types/dictionary-item.interface';

@Injectable()
export class FacilityDictionaryService {
  private readonly facilityDictionaryClient = inject(FacilityDictionaryClient);
  private readonly toastService = inject(ToastService);

  public readonly totalCount: WritableSignal<number> = signal<number>(0);
  public readonly isLoading: WritableSignal<boolean> = signal<boolean>(false);
  public readonly hasLoaded: WritableSignal<boolean> = signal<boolean>(false);

  private readonly skipCount = 10 as const;

  private request: Required<
    Pick<SearchFacilitiesDictionaryRequest, 'skip' | 'take' | 'includeTotalCount'>
  > = {
    skip: 0,
    take: 10,
    includeTotalCount: true,
  };

  private readonly origin$: Subject<void> = new Subject<void>();

  public readonly source$ = this.origin$.pipe(
    startWith(void 0),
    tap((): void => {
      this.isLoading.set(true);
      this.hasLoaded.set(false);
    }),
    exhaustMap(() =>
      this.facilityDictionaryClient.v1FacilityDictionarySearchPost(this.request).pipe(
        tap(({ totalCount }): void => {
          this.totalCount.set(totalCount ?? 0);
        }),
        map(({ items }): IDictionaryItem<string>[] =>
          Array.isArray(items)
            ? items
                .filter((item): boolean => item.id !== undefined && item.id.length > 0)
                .map(
                  (item): IDictionaryItem<string> => ({
                    id: item.id ?? '',
                    name: item.name ?? '',
                  }),
                )
            : [],
        ),
        catchError((err: unknown): Observable<IDictionaryItem<string>[]> => {
          const failedSkip = this.request.skip;

          this.request = {
            ...this.request,
            skip: Math.max(failedSkip - this.skipCount, 0),
          };

          this.toastService.error({
            message: 'monitoring.selector.toast.facilities-fetching-error',
          });

          console.error('Error fetching facility dictionary items:', err);

          return of([]);
        }),
      ),
    ),
    tap((): void => {
      this.isLoading.set(false);
      this.hasLoaded.set(true);
    }),
  );

  public loadMore(): void {
    if (this.isLoading()) return;

    this.request = {
      ...this.request,
      skip: this.request.skip + this.skipCount,
    };

    this.origin$.next();
  }
}
